// Coldroom capacity rollup — pure logic extracted from Coldroom.tsx's capacity
// useMemo so the space arithmetic is unit-testable (PACK coldroom). Given a
// room's grid + each occupied cell's pallet/bin counts, how much space remains?
//
//   Pallet capacity = empty cells (a pallet needs an unoccupied cell).
//   Bin capacity    = empty cells × stack_height + the unfilled slots in cells
//                     that already hold some bins (binCount < stack).
//
// A cell holding a pallet and a cell holding bins are both "occupied" (a cell is
// one or the other, never both, in the floor model).
//
// THE OVERFLOW IS NOT CAPACITY (mig 20261021100001). Stock in a room's overflow
// area is physically in the room and on no cell, so it can neither consume a
// cell nor free one. It is reported ALONGSIDE the grid figures, never folded
// into them:
//
//   • palletRemaining / binRemaining still answer "how many more fit ON THE
//     GRID" — the only question the picker can act on, since a cell is what it
//     hands out. Netting the overflow off would make "3 free" mean nothing.
//   • overflowPallets / overflowBins report what is standing beyond the grid.
//   • `overGrid` is the one derived claim: the room is holding more than its
//     configured map can describe. That is the amber the operator needs, and
//     it is a fact about the room, not a judgement about the operator — a full
//     grid with three pallets in the aisle is a real and legitimate state, and
//     the app's job is to record it, not to argue with it.

export interface ColdroomRoom {
  rows: number
  cols: number
  bin_stack_height: number
}

export interface CellOccupancy {
  palletCount: number
  binCount: number
}

/** What is standing in the room but on no cell. Both default to 0. */
export interface OverflowOccupancy {
  pallets: number
  bins:    number
}

export interface ColdroomCapacity {
  totalCells:      number
  emptyCells:      number
  palletCells:     number
  binCells:        number
  binsStored:      number
  palletRemaining: number
  binRemaining:    number
  palletPctUsed:   number
  binPctUsed:      number
  stackHeight:     number
  // --- beyond the grid -----------------------------------------------------
  overflowPallets: number
  overflowBins:    number
  overflowTotal:   number
  /** Anything at all in the overflow — the room holds more than its map does. */
  overGrid:        boolean
}

export function computeColdroomCapacity(
  room: ColdroomRoom,
  cells: CellOccupancy[],
  overflow: OverflowOccupancy = { pallets: 0, bins: 0 },
): ColdroomCapacity {
  const totalCells = room.rows * room.cols
  const stack      = room.bin_stack_height

  let palletCells = 0
  let binCells    = 0
  let binsStored  = 0
  let binRemainingFromPartial = 0
  for (const v of cells) {
    if (v.palletCount > 0) palletCells += 1
    if (v.binCount > 0) {
      binCells   += 1
      binsStored += v.binCount
      // Partly-filled stack: the unfilled slots are still usable.
      if (v.binCount < stack) binRemainingFromPartial += stack - v.binCount
    }
  }

  const emptyCells      = totalCells - palletCells - binCells
  const palletRemaining = emptyCells
  const binRemaining    = emptyCells * stack + binRemainingFromPartial
  const palletPctUsed   = totalCells === 0 ? 0 : Math.round(((totalCells - emptyCells) / totalCells) * 100)
  const totalBinCapacity = totalCells * stack
  const binPctUsed      = totalBinCapacity === 0 ? 0 : Math.round((binsStored / totalBinCapacity) * 100)

  const overflowPallets = Math.max(0, overflow.pallets)
  const overflowBins    = Math.max(0, overflow.bins)
  const overflowTotal   = overflowPallets + overflowBins

  return {
    totalCells, emptyCells, palletCells, binCells, binsStored,
    palletRemaining, binRemaining, palletPctUsed, binPctUsed,
    stackHeight: stack,
    overflowPallets, overflowBins, overflowTotal,
    overGrid: overflowTotal > 0,
  }
}
